// Documents by id for labels and pickers (spec/dashboard.md section 6.5): the loaded rows and the drafts first, then a
// lazily filled cache fetched in batches through the list's `filter` (id in …), invalidated when the branch head moves.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiClient } from '../api/client';
import { ops } from '../api/operations';
import { entityKey, isTempId, type Workspace } from '../workspace/Workspace';
import type { DocumentLike } from './format';

const UUID = /^[0-9a-f-]{36}$/i;
/** a Balancy `unnyId`: digits or a short token, never a path or a sentence */
const LEGACY_ID = /^[A-Za-z0-9_.-]{1,64}$/;

export class DocumentResolver {
  private readonly cache = new Map<string, DocumentLike | null>(); // null: asked for and missing
  private readonly pending = new Set<string>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly client: ApiClient,
    private readonly path: { org: string; game: string; branch: string },
  ) {}

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Rows a page loaded: known without a fetch. */
  seed(docs: Iterable<DocumentLike>): void {
    for (const d of docs) this.cache.set(d.id, d);
  }

  /**
   * Synchronous: the cached document, or undefined while a fetch is on its way (the listener fires when it lands). Uuids are
   * fetched in batches; a legacy id (an imported profile references documents by `unnyId`, section 11.2) one at a time.
   */
  get(id: string): DocumentLike | undefined {
    if (this.cache.has(id)) return this.cache.get(id) ?? undefined;
    if (!isTempId(id) && (UUID.test(id) || LEGACY_ID.test(id))) this.request(id);
    return undefined;
  }

  /** Whether the id was asked for and answered (found or missing): tells "not found" from "still loading". */
  known(id: string): boolean {
    return this.cache.has(id);
  }

  /** Every id, fetched when missing; resolves when they are known. */
  async load(ids: string[]): Promise<void> {
    for (const id of ids) this.get(id);
    if (this.pending.size === 0) return;
    await this.flush();
  }

  invalidate(): void {
    this.cache.clear();
    this.emit();
  }

  private request(id: string): void {
    if (this.pending.has(id)) return;
    this.pending.add(id);
    if (this.timer === null) this.timer = setTimeout(() => void this.flush(), 20);
  }

  private async flush(): Promise<void> {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const ids = Array.from(this.pending).slice(0, 50);
    if (ids.length === 0) return;
    for (const id of ids) this.pending.delete(id);
    const uuids = ids.filter((id) => UUID.test(id));
    const legacy = ids.filter((id) => !UUID.test(id));
    try {
      if (uuids.length > 0) {
        const page = await this.client.data(ops.listDocuments, { path: this.path, query: { filter: JSON.stringify([{ field: 'id', op: 'in', value: uuids }]), limit: 200 } });
        for (const d of page.items) this.cache.set(d.id, d as unknown as DocumentLike);
      }
      await Promise.all(
        legacy.map(async (id) => {
          try {
            const d = await this.client.data(ops.getDocument, { path: { ...this.path, document: `unny:${id}` } });
            this.cache.set(id, d as unknown as DocumentLike);
          } catch {
            this.cache.set(id, null);
          }
        }),
      );
      for (const id of ids) if (!this.cache.has(id)) this.cache.set(id, null);
    } catch {
      for (const id of ids) if (!this.cache.has(id)) this.cache.set(id, null); // a missing label, retried after the next head move
    }
    this.emit();
    if (this.pending.size > 0) await this.flush();
  }

  private emit(): void {
    for (const l of Array.from(this.listeners)) l();
  }
}

const resolvers = new Map<string, DocumentResolver>();

export function resolverFor(client: ApiClient, org: string, game: string, branch: string): DocumentResolver {
  const key = `${org}/${game}/${branch}`;
  let r = resolvers.get(key);
  if (!r) {
    r = new DocumentResolver(client, { org, game, branch });
    resolvers.set(key, r);
  }
  return r;
}

/** For tests: forget every cached document. */
export function resetResolvers(): void {
  resolvers.clear();
}

/** The resolve function pages hand to the formatters: drafts first (a temp id resolves to its draft), then the cache; re-renders when a fetch lands. */
export function useDocumentResolver(client: ApiClient, org: string, game: string, branch: string, head: string | null, workspace?: Workspace | null): { resolve: (id: string) => DocumentLike | undefined; resolver: DocumentResolver; version: number } {
  const resolver = useMemo(() => resolverFor(client, org, game, branch), [client, org, game, branch]);
  const [version, setVersion] = useState(0);
  useEffect(() => resolver.subscribe(() => setVersion((v) => v + 1)), [resolver]);
  useEffect(() => {
    if (head) resolver.invalidate();
  }, [resolver, head]);
  const resolve = useCallback(
    (id: string): DocumentLike | undefined => {
      const draft = workspace?.getDraft(entityKey('document', id));
      if (draft && draft.status !== 'deleted') return draft.current as unknown as DocumentLike;
      return resolver.get(id);
    },
    [resolver, workspace],
  );
  return { resolve, resolver, version };
}
